import { Box, Text, Flex, Badge, ThemeIcon } from "@mantine/core";
import type { ReactNode } from "react";
import {
  IconShieldLock,
  IconPresentation,
  IconTestPipe,
  IconMessages,
  IconPlugConnected,
  IconPuzzle2,
  IconReportAnalytics,
  IconSchool,
  IconTool,
  IconDots,
} from "@tabler/icons-react";
import {
  CUSTOMER_OFFER_OTHER_LABEL,
  CUSTOMER_OFFER_PRESET_LABELS,
  parseCustomerOfferHighlightFromApi,
} from "@/shared/constants/customerOfferPresets";

export const WHAT_WE_OFFER_PURPLE = "#6d28d9";
/** Deep heading / icon tone */
const WHAT_WE_OFFER_INK = "#3730a3";
const WHAT_WE_OFFER_PURPLE_LIGHT = "#ede9fe";
const WHAT_WE_OFFER_SURFACE = "#f5f3ff";
const OFFER_TILE_SIZE = 58;

const CUSTOMER_OFFER_ICONS = [
  IconPresentation,
  IconTestPipe,
  IconMessages,
  IconPlugConnected,
  IconPuzzle2,
  IconReportAnalytics,
  IconSchool,
  IconTool,
  IconDots,
] as const;

const DEFAULT_DESCRIPTION_READ =
  "Explore the products, services, support and collaboration advantages this company offers.";

type WhatWeOfferChromeProps = {
  children: ReactNode;
  /** Yellow “New” badge (e.g. profile builder); omit on public profile. */
  showNewBadge?: boolean;
  description?: ReactNode;
};

export function WhatWeOfferChrome({ children, showNewBadge, description }: WhatWeOfferChromeProps) {
  return (
    <Box
      p={{ base: "lg", sm: "xl" }}
      style={{
        border: `2px dashed rgba(109, 40, 217, 0.42)`,
        borderRadius: 16,
        background: `linear-gradient(165deg, ${WHAT_WE_OFFER_SURFACE} 0%, ${WHAT_WE_OFFER_PURPLE_LIGHT} 42%, #fafbff 100%)`,
        boxShadow: "0 4px 24px rgba(76, 29, 149, 0.07), 0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Flex align="flex-start" gap={{ base: "md", sm: "lg" }} wrap="nowrap">
        <ThemeIcon
          size={52}
          radius="50%"
          variant="filled"
          style={{
            flexShrink: 0,
            backgroundColor: WHAT_WE_OFFER_PURPLE,
            boxShadow: "0 4px 14px rgba(109, 40, 217, 0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <IconShieldLock size={28} stroke={1.35} color="#ffffff" />
        </ThemeIcon>
        <Box style={{ minWidth: 0, flex: 1, paddingTop: 2 }}>
          <Flex align="center" gap="sm" wrap="wrap" rowGap={6}>
            <Text
              fw={800}
              fz={{ base: "lg", sm: 20 }}
              c={WHAT_WE_OFFER_INK}
              style={{ letterSpacing: "-0.02em", lineHeight: 1.2 }}
            >
              What We Offer
            </Text>
            {showNewBadge ? (
              <Badge
                size="sm"
                radius="xl"
                variant="filled"
                px={10}
                h={22}
                styles={{
                  root: {
                    background: "linear-gradient(180deg, #fde047 0%, #facc15 100%)",
                    color: "#1c1917",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontSize: 10,
                    border: "1px solid rgba(234, 179, 8, 0.5)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  },
                }}
              >
                New
              </Badge>
            ) : null}
          </Flex>
          <Text
            fz="sm"
            c="#64748b"
            lh={1.55}
            mt={10}
            maw={720}
            style={{ fontWeight: 400 }}
          >
            {description ?? DEFAULT_DESCRIPTION_READ}
          </Text>
        </Box>
      </Flex>

      <Box
        mt={{ base: "lg", sm: "xl" }}
        pt={{ base: "lg", sm: "xl" }}
        style={{
          borderTop: "1px solid rgba(109, 40, 217, 0.12)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

type WhatWeOfferIconTilesProps = {
  selectedLabel: string | null;
  /** When true, non-selected tiles are dimmed (read-only profile). */
  dimUnselected?: boolean;
};

export function WhatWeOfferIconTiles({
  selectedLabel,
  dimUnselected = false,
}: WhatWeOfferIconTilesProps) {
  const tileInner = (label: string, index: number, selected: boolean) => {
    const IconCmp = CUSTOMER_OFFER_ICONS[index] ?? IconDots;
    const dim = dimUnselected && !selected;
    return (
      <Flex direction="column" align="center" gap={10}>
        <Flex
          align="center"
          justify="center"
          style={{
            width: OFFER_TILE_SIZE,
            height: OFFER_TILE_SIZE,
            borderRadius: "50%",
            border: selected
              ? `2px solid ${WHAT_WE_OFFER_PURPLE}`
              : "1.5px solid rgba(196, 181, 253, 0.95)",
            backgroundColor: selected ? "#ffffff" : "rgba(255,255,255,0.92)",
            boxShadow: selected
              ? `0 0 0 4px ${WHAT_WE_OFFER_PURPLE_LIGHT}, 0 6px 16px rgba(109, 40, 217, 0.12)`
              : "0 2px 8px rgba(15, 23, 42, 0.04)",
            opacity: dim ? 0.38 : 1,
            transition: "box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.2s ease",
          }}
        >
          <IconCmp
            size={selected ? 27 : 25}
            color={dim ? "#a78bfa" : WHAT_WE_OFFER_PURPLE}
            stroke={selected ? 1.5 : 1.35}
          />
        </Flex>
        <Text
          ta="center"
          fz={10}
          fw={selected ? 700 : 600}
          c={selected ? WHAT_WE_OFFER_INK : dim ? "#94a3b8" : "#475569"}
          lh={1.3}
          style={{ maxWidth: 104, wordBreak: "break-word", opacity: dim ? 0.5 : 1 }}
        >
          {label}
        </Text>
      </Flex>
    );
  };

  return (
    <Flex
      gap={{ base: 12, sm: 16 }}
      wrap="wrap"
      justify={{ base: "center", sm: "flex-start" }}
      rowGap={{ base: 16, sm: 18 }}
    >
      {CUSTOMER_OFFER_PRESET_LABELS.map((label, index) => {
        const selected = selectedLabel === label;
        return (
          <Box key={label} style={{ width: 104, flex: "0 0 auto" }}>
            {tileInner(label, index, selected)}
          </Box>
        );
      })}
    </Flex>
  );
}

/** Read-only “What We Offer” for profile / discover views (single `customerOfferHighlight` string). */
export function CustomerOfferHighlightReadOnly({
  value,
  showNewBadge = false,
}: {
  value: string | null | undefined;
  showNewBadge?: boolean;
}) {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return null;

  const { preset, otherText } = parseCustomerOfferHighlightFromApi(trimmed);
  const selectedLabel = preset ? preset : null;

  return (
    <Box mb={{ base: "lg", sm: "xl" }}>
      <WhatWeOfferChrome showNewBadge={showNewBadge}>
        <WhatWeOfferIconTiles selectedLabel={selectedLabel} dimUnselected />
        {preset === CUSTOMER_OFFER_OTHER_LABEL && otherText.trim() ? (
          <Box
            mt={{ base: "md", sm: "lg" }}
            p={{ base: "md", sm: "lg" }}
            style={{
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: 12,
              border: "1px solid rgba(196, 181, 253, 0.55)",
              boxShadow: "0 2px 12px rgba(76, 29, 149, 0.06)",
              borderLeft: `4px solid ${WHAT_WE_OFFER_PURPLE}`,
            }}
          >
            <Text size="xs" c={WHAT_WE_OFFER_INK} tt="uppercase" fw={700} mb={8} style={{ letterSpacing: "0.04em" }}>
              Custom offer
            </Text>
            <Text size="sm" c="#334155" lh={1.6} style={{ whiteSpace: "pre-wrap" }}>
              {otherText}
            </Text>
          </Box>
        ) : null}
      </WhatWeOfferChrome>
    </Box>
  );
}
